import {
  map, indexOf, cloneDeep, assign,
  keys, includes, orderBy, values,
  isFinite, each, filter, isNil,
} from 'lodash';
import { formatMinutesAndSeconds } from '@/utils/number';
import Event from './event';
import { shallowMergeEventDetails } from './event-details-update';

export const ID = '01330fb1-5b81-4b90-89fe-7f3796d8eb21';

const Periods = {
  PRE_GAME: 'PRE_GAME',
  FIRST_SET: 'FIRST_SET',
  SECOND_SET: 'SECOND_SET',
  THIRD_SET: 'THIRD_SET',
  FOURTH_SET: 'FOURTH_SET',
  FIFTH_SET: 'FIFTH_SET',
  POST_GAME: 'POST_GAME',
  GAME_ABANDONED: 'GAME_ABANDONED',
};

const activePeriods = [
  Periods.FIRST_SET,
  Periods.SECOND_SET,
  Periods.THIRD_SET,
  Periods.FOURTH_SET,
  Periods.FIFTH_SET,
];

const livePeriods = [
  Periods.FIRST_SET,
  Periods.SECOND_SET,
  Periods.THIRD_SET,
  Periods.FOURTH_SET,
  Periods.FIFTH_SET,
];

const getLivePeriods = () => livePeriods;

const PeriodLabels = {
  TOTAL: 'Match',
  N_A: 'N/A',
  [Periods.PRE_GAME]: 'Pregame',
  [Periods.FIRST_SET]: 'S1',
  [Periods.SECOND_SET]: 'S2',
  [Periods.THIRD_SET]: 'S3',
  [Periods.FOURTH_SET]: 'S4',
  [Periods.FIFTH_SET]: 'S5',
  [Periods.POST_GAME]: 'Finished',
  [Periods.GAME_ABANDONED]: 'Suspended',
};

const PeriodAbbreviations = {
  TOTAL: 'Match',
  N_A: 'N/A',
  [Periods.PRE_GAME]: 'Pregame',
  [Periods.FIRST_SET]: 'S1',
  [Periods.SECOND_SET]: 'S2',
  [Periods.THIRD_SET]: 'S3',
  [Periods.FOURTH_SET]: 'S4',
  [Periods.FIFTH_SET]: 'S5',
  [Periods.POST_GAME]: 'Finished',
  [Periods.GAME_ABANDONED]: 'Suspended',
};

const isPrematch = (event) => Event.isPrematch(event) || event.period === Periods.PRE_GAME;

const isLive = (event) => Event.isLive(event) && !includes([Periods.POST_GAME, Periods.GAME_ABANDONED], event.period);

const getPeriodTime = ({ totalSeconds }) => {
  if (!totalSeconds) return '0:00';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const formattedMinutes = minutes.toString().padStart(2, '0');
  return `${hours}:${formattedMinutes}`;
};

const getPeriodLabel = ({ period, setDurations }) => {
  const label = PeriodLabels[period] ?? '';

  if (!includes(activePeriods, period)) return label;
  let totalSeconds = 0;
  each(setDurations, (duration) => {
    totalSeconds += duration;
  });
  if (!totalSeconds) return `${label} · 0:00`;
  const time = getPeriodTime({ totalSeconds });
  return `${label} · ${time}`;
};

const getPeriodValue = (period) => {
  if (period === 'TOTAL') return 0;
  return indexOf(keys(Periods), period) + 1;
};

const getPeriodDuration = (period, setDurations, matchTime) => {
  if (period === 'TOTAL') return getPeriodTime({ totalSeconds: matchTime });
  const totalSeconds = setDurations[period] || 0;
  return getPeriodTime({ totalSeconds });
};

const getDetails = (event, prematch) => {
  const scorePerPeriod = map(
    Event.getAvailablePeriods({
      allPeriods: keys(Periods),
      scorePerPeriod: event.home.scorePerPeriod,
    }),
    (period) => {
      const isVisible = !prematch && getPeriodValue(event.period) >= getPeriodValue(period);
      return {
        code: period,
        label: `${PeriodAbbreviations[period]} (${getPeriodDuration(period, event.setDurations)})`,
        homeValue: isVisible ? event.home.scorePerPeriod[period] || 0 : '',
        awayValue: isVisible ? event.away.scorePerPeriod[period] || 0 : '',
      };
    },
  );

  return [
    {
      code: 'TOTAL',
      label: `${PeriodAbbreviations.TOTAL} (${getPeriodDuration('TOTAL', event.setDurations, event.matchTime)})`,
      homeValue: prematch ? '-' : event.home.score,
      awayValue: prematch ? '-' : event.away.score,
    },
    ...scorePerPeriod,
  ];
};

const getDetailedScoreboard = (currentPeriod, home, away) => {
  const allPeriods = Periods;
  if (!home?.scorePerPeriod || !away?.scorePerPeriod) return [];
  const filteredPeriods = filter(allPeriods, (set) => !isNil(home?.scorePerPeriod[set]));
  return map(filteredPeriods, (set) => {
    const setScore = `${home?.scorePerPeriod[set]}:${away?.scorePerPeriod[set]}`;
    return {
      label: set,
      setScore,
      isCurrent: currentPeriod === set,
    };
  });
};

const increaseGameScore = (lastPoint, point) => {
  if (point === 'HOME') {
    if (lastPoint.homeScore === 0) {
      return {
        homeScore: 15,
        awayScore: lastPoint.awayScore,
      };
    }
    if (lastPoint.homeScore === 15) {
      return {
        homeScore: 30,
        awayScore: lastPoint.awayScore,
      };
    }
    if (lastPoint.homeScore === 30) {
      return {
        homeScore: 40,
        awayScore: lastPoint.awayScore,
      };
    }
    if (lastPoint.homeScore === 40) {
      if (lastPoint.awayScore === 'A') {
        return {
          homeScore: 40,
          awayScore: 40,
        };
      } if (lastPoint.awayScore === 40) {
        return {
          homeScore: 'A',
          awayScore: 40,
        };
      }
      return {
        homeScore: 'GAME_WON',
        awayScore: 'GAME_LOST',
      };
    }
    if (lastPoint.homeScore === 'A') {
      return {
        homeScore: 'GAME_WON',
        awayScore: 'GAME_LOST',
      };
    }
  }
  if (lastPoint.awayScore === 0) {
    return {
      homeScore: lastPoint.homeScore,
      awayScore: 15,
    };
  }
  if (lastPoint.awayScore === 15) {
    return {
      homeScore: lastPoint.homeScore,
      awayScore: 30,
    };
  }
  if (lastPoint.awayScore === 30) {
    return {
      homeScore: lastPoint.homeScore,
      awayScore: 40,
    };
  }
  if (lastPoint.awayScore === 40) {
    if (lastPoint.homeScore === 'A') {
      return {
        homeScore: 40,
        awayScore: 40,
      };
    } if (lastPoint.homeScore === 40) {
      return {
        homeScore: 40,
        awayScore: 'A',
      };
    }
    return {
      homeScore: 'GAME_LOST',
      awayScore: 'GAME_WON',
    };
  }
  if (lastPoint.awayScore === 'A') {
    return {
      homeScore: 'GAME_LOST',
      awayScore: 'GAME_WON',
    };
  }
  return {
    homeScore: lastPoint.homeScore,
    awayScore: lastPoint.awayScore,
  };
};

const updateEvent = (eventDetails, payload) => shallowMergeEventDetails(eventDetails, {
      setsWon: payload.state?.setsWon,
      gamesWonPerSet: payload.state?.gamesWonPerSet,
      pointsWonPerGame: payload.state?.pointsWonPerGame,
      acesInMatch: payload.state?.acesInMatch,
      acesPerSet: payload.state?.acesPerSet,
      doubleFaultsInMatch: payload.state?.doubleFaultsInMatch,
      doubleFaultsPerPeriod: payload.state?.doubleFaultsPerSet,
      period: payload.state?.period || 'N_A',
      currentServingTeam: payload.state?.currentServingTeam,
      currentGameScore: payload.state?.currentGameScore,
      currentGameNumber: payload.state?.currentGameNumber,
      isPointInProgress: payload.state?.isPointInProgress,
      secondServe: payload.state?.secondServe,
      noAdvantage: payload.rules?.noAdvantage,
      setsToPlay: payload.rules?.setsToPlay,
      lastSetSuperTiebreak: payload.rules?.lastSetSuperTiebreak,
      lastSetSuperTiebreakPoints: payload.rules?.lastSetSuperTiebreakPoints,
      eventInBreak: payload.state?.eventInBreak,
      eventPreMatch: payload.state?.eventPreMatch,
      riskFlags: payload.state?.riskFlags || [],
      matchTime: payload.state?.matchTime || 0,
      setDurations: payload.state?.setDurations || [],
      breakPointsPlayedInMatch: payload.state?.breakPointsPlayedInMatch,
      breakPointsWonInMatch: payload.state?.breakPointsWonInMatch,
      breakPointsPlayedPerSet: payload.state?.breakPointsPlayedPerSet,
      breakPointsWonPerSet: payload.state?.breakPointsWonPerSet,
      breakPointsWonPercentageInMatch: payload.state?.breakPointsWonPercentageInMatch,
      breakServePointsWonPercentagePerSet: payload.state?.breakServePointsWonPercentagePerSet,
      firstServePercentageInMatch: payload.state?.firstServePercentageInMatch,
      firstServePercentagePerSet: payload.state?.firstServePercentagePerSet,
      firstServePointsPlayedInMatch: payload.state?.firstServePointsPlayedInMatch,
      firstServePointsPlayedPerSet: payload.state?.firstServePointsPlayedPerSet,
      firstServePointsWonInMatch: payload.state?.firstServePointsWonInMatch,
      firstServePointsWonPerSet: payload.state?.firstServePointsWonPerSet,
      firstServePointsWonPercentageInMatch: payload.state?.firstServePointsWonPercentageInMatch,
      firstServePointsWonPercentagePerSet: payload.state?.firstServePointsWonPercentagePerSet,
      secondServePercentageInMatch: payload.state?.secondServePercentageInMatch,
      secondServePercentagePerSet: payload.state?.secondServePercentagePerSet,
      secondServePointsPlayedInMatch: payload.state?.secondServePointsPlayedInMatch,
      secondServePointsPlayedPerSet: payload.state?.secondServePointsPlayedPerSet,
      secondServePointsWonInMatch: payload.state?.secondServePointsWonInMatch,
      secondServePointsWonPerSet: payload.state?.secondServePointsWonPerSet,
      secondServePointsWonPercentageInMatch: payload.state?.secondServePointsWonPercentageInMatch,
      secondServePointsWonPercentagePerSet: payload.state?.secondServePointsWonPercentagePerSet,
});

const formatPeriodTime = ({ secondsLeftInPeriod }) => formatMinutesAndSeconds(secondsLeftInPeriod);

const formatPeriodMinute = ({ secondsLeftInPeriod }) => formatMinutesAndSeconds(secondsLeftInPeriod);

const formatPeriodLabel = ({ period }) => PeriodLabels[period] ?? period;

const formatPeriodAbbreviation = ({ period }) => PeriodAbbreviations[period] ?? period;

const formatPeriod = (details, options) => {
  const totalSeconds = details?.matchTime || 0;
  const formatLabel = options?.formatLabel ?? formatPeriodLabel;
  const separator = options?.separator ?? '·';

  const label = formatLabel(details);
  const time = getPeriodTime({ totalSeconds });

  return (includes(activePeriods, details.period) ? `${label} ${separator} ${time}` : label);
};

const calculatePeriodProgress = ({
  period,
  periodLengthInMinutes,
  overtimeLengthInMinutes,
  secondsLeftInPeriod,
}) => {
  if (!includes(activePeriods, period)) return -1;
  if (includes(PeriodAbbreviations[period], 'OT')) return (((overtimeLengthInMinutes * 60) - secondsLeftInPeriod) / (overtimeLengthInMinutes * 60)) * 100;
  return (((periodLengthInMinutes * 60) - secondsLeftInPeriod) / (periodLengthInMinutes * 60)) * 100;
};

const statisticTypes = {
  ACES: 'ACES',
  DOUBLE_FAULTS: 'DOUBLE_FAULTS',
  BREAK_POINTS_PLAYED: 'BREAK_POINTS_PLAYED',
  BREAK_POINTS_WON: 'BREAK_POINTS_WON',
  FIRST_SERVE_PERCENTAGE: 'FIRST_SERVE_PERCENTAGE',
  FIRST_SERVE_IN: 'FIRST_SERVE_IN',
  FIRST_SERVE_WON: 'FIRST_SERVE_WON',
  FIRST_SERVE_WON_PERCENTAGE: 'FIRST_SERVE_WON_PERCENTAGE',
  SECOND_SERVE_PERCENTAGE: 'SECOND_SERVE_PERCENTAGE',
  SECOND_SERVE_IN: 'SECOND_SERVE_IN',
  SECOND_SERVE_WON: 'SECOND_SERVE_WON',
  SECOND_SERVE_WON_PERCENTAGE: 'SECOND_SERVE_WON_PERCENTAGE',
};

const createStatisticsConfiguration = (event) => ({
  periods: Event.getAvailablePeriods({
    allPeriods: keys(Periods),
    scorePerPeriod: event.home.scorePerPeriod,
  }),
  fields: {
    [statisticTypes.ACES]: 'acesPerPeriod',
    [statisticTypes.DOUBLE_FAULTS]: 'doubleFaultsPerPeriod',
    [statisticTypes.BREAK_POINTS_PLAYED]: 'breakPointsPlayedPerPeriod',
    [statisticTypes.BREAK_POINTS_WON]: 'breakPointsWonPerPeriod',
    [statisticTypes.FIRST_SERVE_PERCENTAGE]: 'firstServePercentagePerPeriod',
    [statisticTypes.FIRST_SERVE_IN]: 'firstServePointsPlayedPerPeriod',
    [statisticTypes.FIRST_SERVE_WON]: 'firstServePointsWonPerPeriod',
    [statisticTypes.FIRST_SERVE_WON_PERCENTAGE]: 'firstServePointsWonPercentagePerPeriod',
    [statisticTypes.SECOND_SERVE_PERCENTAGE]: 'secondServePercentagePerPeriod',
    [statisticTypes.SECOND_SERVE_IN]: 'secondServePointsPlayedPerPeriod',
    [statisticTypes.SECOND_SERVE_WON]: 'secondServePointsWonPerPeriod',
    [statisticTypes.SECOND_SERVE_WON_PERCENTAGE]: 'secondServePointsWonPercentagePerPeriod',
  },
  totalFields: {
    [statisticTypes.ACES]: 'aces',
    [statisticTypes.DOUBLE_FAULTS]: 'doubleFaults',
    [statisticTypes.BREAK_POINTS_PLAYED]: 'breakPointsPlayedInMatch',
    [statisticTypes.BREAK_POINTS_WON]: 'breakPointsWonInMatch',
    [statisticTypes.FIRST_SERVE_PERCENTAGE]: 'firstServePercentageInMatch',
    [statisticTypes.FIRST_SERVE_IN]: 'firstServePointsPlayedInMatch',
    [statisticTypes.FIRST_SERVE_WON]: 'firstServePointsWonInMatch',
    [statisticTypes.FIRST_SERVE_WON_PERCENTAGE]: 'firstServePointsWonPercentageInMatch',
    [statisticTypes.SECOND_SERVE_PERCENTAGE]: 'secondServePercentageInMatch',
    [statisticTypes.SECOND_SERVE_IN]: 'secondServePointsPlayedInMatch',
    [statisticTypes.SECOND_SERVE_WON]: 'secondServePointsWonInMatch',
    [statisticTypes.SECOND_SERVE_WON_PERCENTAGE]: 'secondServePointsWonPercentageInMatch',
  },
  labels: {
    [statisticTypes.ACES]: 'Aces',
    [statisticTypes.DOUBLE_FAULTS]: 'Double faults',
    [statisticTypes.BREAK_POINTS_PLAYED]: 'Break points',
    [statisticTypes.BREAK_POINTS_WON]: 'Break points won',
    [statisticTypes.FIRST_SERVE_PERCENTAGE]: 'First serve %',
    [statisticTypes.FIRST_SERVE_IN]: 'First serve in',
    [statisticTypes.FIRST_SERVE_WON]: 'First serve won',
    [statisticTypes.FIRST_SERVE_WON_PERCENTAGE]: 'First serve won %',
    [statisticTypes.SECOND_SERVE_PERCENTAGE]: 'Second serve %',
    [statisticTypes.SECOND_SERVE_IN]: 'Second serve in',
    [statisticTypes.SECOND_SERVE_WON]: 'Second serve won',
    [statisticTypes.SECOND_SERVE_WON_PERCENTAGE]: 'Second serve won %',
  },
  formatters: {
    [statisticTypes.FIRST_SERVE_PERCENTAGE]: (value) => (isFinite(value) ? (value * 100).toFixed(1) : '-'),
    [statisticTypes.FIRST_SERVE_WON_PERCENTAGE]: (value) => (isFinite(value) ? (value * 100).toFixed(1) : '-'),
    [statisticTypes.SECOND_SERVE_PERCENTAGE]: (value) => (isFinite(value) ? (value * 100).toFixed(1) : '-'),
    [statisticTypes.SECOND_SERVE_WON_PERCENTAGE]: (value) => (isFinite(value) ? (value * 100).toFixed(1) : '-'),
  },
  totalFormatters: {},
  transform({ period, value }) {
    if (period === 'TOTAL') return value ?? NaN;
    return value?.[period] ?? NaN;
  },
});

const getAvailablePeriods = (event) => [
  {
    id: 'TOTAL',
    label: `Full event (${getPeriodDuration('TOTAL', event.setDurations, event.matchTime)})`,
  },
  ...map(
    Event.getAvailablePeriods({
      allPeriods: keys(Periods),
      scorePerPeriod: event.home.scorePerPeriod,
    }),
    (period) => ({
      id: period,
      label: `${PeriodLabels[period]} (${getPeriodDuration(period, event.setDurations)})`,
    }),
  ),
];

const getPeriodIndex = (periodCode) => {
  if (periodCode === 'TOTAL') return 0;
  return indexOf(values(Periods), periodCode) + 1;
};

const sortPeriods = (periods) => orderBy(periods, (period) => getPeriodIndex(period));

export default {
  ID,
  Periods,
  activePeriods,
  livePeriods,
  getLivePeriods,
  PeriodAbbreviations,
  PeriodLabels,
  isPrematch,
  isLive,
  getPeriodTime,
  getPeriodLabel,
  getPeriodValue,
  getDetails,
  updateEvent,
  formatPeriodTime,
  formatPeriodMinute,
  formatPeriodLabel,
  formatPeriodAbbreviation,
  formatPeriod,
  calculatePeriodProgress,
  createStatisticsConfiguration,
  getAvailablePeriods,
  getPeriodIndex,
  sortPeriods,
  getDetailedScoreboard,
  increaseGameScore,
};
